import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { LayoutItem, ThreeDPanelState } from "@/shared/state/visualization";

import { useWorkspaceTimer } from "../../WorkspaceCtx";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { PanelHeader } from "../PanelHeader";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { BabylonManager } from "./BabylonManager";
import { useBabylonTheme } from "./BabylonManager/useBabylonTheme";
import { DataServerManager } from "./DataServer/DataServerManager";
import { FreeCamHintAlert } from "./FreeCamHintAlert";
import { ModelLoader } from "./ModelLoader";
import {
  ModelLoadErrorAlert,
  type ModelLoadFailure,
} from "./ModelLoadErrorAlert";
import { ModeToggleButton } from "./ModeToggleButton";
import { RecenterButton } from "./RecenterButton";
import styles from "./ThreeDPanel.module.css";

interface ThreeDPanelProps {
  layout: LayoutItem;
  state: ThreeDPanelState;
}

export function ThreeDPanel({ layout, state }: ThreeDPanelProps) {
  const theme = useBabylonTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [manager, setManager] = React.useState<BabylonManager | null>(null);
  const [modelFailures, setModelFailures] = React.useState<ModelLoadFailure[]>(
    [],
  );
  const timer = useWorkspaceTimer();
  const initTheme = React.useRef(theme);
  const initState = React.useRef(state);

  const { files } = useDomainServices();
  // One loader per file service, not per render: the manager captures it in
  // its context for its whole lifetime.
  const modelLoader = React.useMemo(() => new ModelLoader(files), [files]);
  const initModelLoader = React.useRef(modelLoader);

  const initManager = React.useCallback(
    (
      dataServerManager: DataServerManager,
      time: bigint,
      canvas: HTMLCanvasElement,
    ) => {
      try {
        const manager = new BabylonManager({
          dataServer: dataServerManager,
          modelLoader: initModelLoader.current,
          canvas,
          time,
          initialState: initState.current,
          theme: initTheme.current,
          callbacks: {
            onLoadingStatusChanged: (loadingStatus) => {
              setIsLoading(loadingStatus);
            },
            onModelLoadFailed: (modelId, modelError) => {
              // Replace any earlier failure for the same model so a
              // reconfigured-then-failed model is reported once, not twice.
              setModelFailures((failures) => [
                ...failures.filter((failure) => failure.modelId !== modelId),
                { modelId, message: modelError.message },
              ]);
            },
          },
        });
        setManager(manager);
        return manager;
      } catch (e) {
        // This is normally a WebGL Not Supported error, which is unrecoverable.
        ErrorMonitoringService.captureError(e);
        setError(e as Error);
        // Short-circuit because the panel is not viable.
        return;
      }
    },
    [setIsLoading, setManager, setError, setModelFailures],
  );

  React.useEffect(
    function onStateUpdated() {
      try {
        manager?.setState(state);
      } catch (e) {
        ErrorMonitoringService.captureError(e);
      }
    },
    [state, manager],
  );

  // Serialized rather than a Set so the effect below has a stable dependency.
  // JSON rather than a joined string because a node's id embeds a file path,
  // which may contain any delimiter one might pick.
  const drawnModelNodeIds = React.useMemo(
    () =>
      JSON.stringify(
        Object.values(state.data.entities)
          .flatMap((entity) => {
            const mesh = entity.mesh;
            return mesh?.type === "model_node"
              ? [
                  `${mesh.file.relativePath ?? mesh.file.fileId}#${mesh.nodeName}`,
                ]
              : [];
          })
          .sort(),
      ),
    [state.data.entities],
  );
  React.useEffect(
    function forgetFailuresForRemovedNodes() {
      // A node whose entity was deleted — or whose mesh was repointed, which
      // restarts its load — should not keep showing its old error. Failures for
      // nodes still being drawn survive until they reload.
      const stillDrawn = new Set(JSON.parse(drawnModelNodeIds) as string[]);
      setModelFailures((failures) =>
        failures.every((failure) => stillDrawn.has(failure.modelId))
          ? failures
          : failures.filter((failure) => stillDrawn.has(failure.modelId)),
      );
    },
    [drawnModelNodeIds],
  );

  React.useEffect(
    function updateTheme() {
      manager?.setTheme(theme);
    },
    [theme, manager],
  );

  const [dataClient, setDataClient] = React.useState<DataServerManager | null>(
    null,
  );
  React.useEffect(
    function initDataServer() {
      const controller = new AbortController();
      DataServerManager.create(controller)
        .then((dsm) => {
          setDataClient(dsm);
        })
        .catch((e) => {
          if (!controller.signal.aborted && !isAbortError(e)) {
            ErrorMonitoringService.captureError(e);
          }
        });
      return () => {
        controller.abort();
      };
    },
    [setDataClient],
  );

  React.useEffect(
    function init() {
      if (canvasRef.current === undefined || dataClient === null) {
        return;
      }
      const canvas = canvasRef.current;

      // Absorb canvas mouse scrolls.
      function absorbMouseWheelEvents(event: MouseEvent) {
        event.preventDefault();
        return false;
      }

      canvas.addEventListener("wheel", absorbMouseWheelEvents, false);
      canvas.addEventListener("drag", absorbMouseWheelEvents, false);
      canvas.addEventListener("click", absorbMouseWheelEvents, false);

      const manager = initManager(dataClient, timer.currentTime, canvas);
      const tickListener = manager
        ? timer.addListener("tick", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      const seekListener = manager
        ? timer.addListener("seek", function onTick(timer) {
            manager.setTime(timer.currentTime);
          })
        : undefined;
      return () => {
        setManager(null);
        setDataClient(null);
        manager?.dispose();

        canvas.removeEventListener("wheel", absorbMouseWheelEvents);
        canvas.removeEventListener("drag", absorbMouseWheelEvents);
        canvas.removeEventListener("click", absorbMouseWheelEvents);
        if (tickListener) {
          timer.removeListener(tickListener);
        }
        if (seekListener) {
          timer.removeListener(seekListener);
        }
      };
    },
    [dataClient, initManager, setManager, timer],
  );

  return (
    <PanelLayout
      header={
        <PanelHeader
          additionalTools={
            <>
              <RecenterButton manager={manager} state={state} />
              <ModeToggleButton
                panelId={state.id}
                manager={manager}
                state={state}
              />
            </>
          }
          className={styles.panelHeader}
          state={state}
        />
      }
      isLoading={isLoading && !error}
      layout={layout}
      state={state}
    >
      {!error && (
        <canvas
          className={styles.canvas}
          ref={(ref) => (canvasRef.current = ref ?? undefined)}
        />
      )}
      <FreeCamHintAlert
        cameraType={state.config?.camera?.type}
        className={styles.freeCamHint}
      />
      <ModelLoadErrorAlert
        failures={modelFailures}
        onDismiss={() => setModelFailures([])}
        className={styles.modelLoadError}
      />
      {/* TODO(dave): enable onClearError in cases where the error is recoverable. */}
      {error && <RenderingError error={error} />}
      <DataNotFoundMessage panelId={state.id} />
    </PanelLayout>
  );
}
